import React, { useEffect, useState, useContext } from 'react';
import {
  CallLogTableDataWeb,
  ModalCallSummary,
  Searchbar,
} from '../../features';
import { CommonFilter } from '../../features/Jobs/commonFilter';
import {
  useFunctionalityBookingSummary,
  useFunctionalityCallLog,
  useFunctionalityCallSummary,
  useFunctionalityColumnSelections,
  useFunctionalityUserProfile,
} from '../hooks';
import { useLocation } from 'react-router-dom';
import {
  CallLogPagedResponseModel,
  CallSummaryResponseModel,
} from '../../models/api/Call';
import {
  AppConstants,
  CallLogType,
  CallStatusType,
  ColumnSelectionTableType,
  ExportTableType,
  PaginationSettings,
  topLoaderProgress,
} from '../../configs';
import LoadingBar from 'react-top-loading-bar';
import { ActionAuth, useAppSelector, VRIAppStateType, useAppDispatch, AudioDeviceContext } from '../../core';
import { useFunctionalityCallLogColumns } from '../hooks/functionalityCallLogColumns';
import { CallLogInfoBox } from '../../features/Common/Components/CallLogInfoBox';
import { ApiSchemaMenuSettingsData } from '../../models/api/menuSettings';
import ReactDOM from 'react-dom';
import { ColumnSelectionsModel } from '../../models';
import { ApiSchemaFilter } from '../../models/api/filter';
import useFunctionalityMediaPermissions from '../hooks/mediaPermission/functionalityMediaPermission';
import { controlBrowserBackAndReloadButton } from '../../helpers';
import { isElectron } from '../../utils';

export function PageCallLog(): JSX.Element {

  const [loaderProgress, setLoaderProgress] = React.useState(0);
  const [show, setShow] = React.useState(false);
  const [isShowCommonFilter, setIsShowCommonFilter] = useState<boolean>(false);
  const [callSummaryData, setCallSummaryData] =
    React.useState<CallSummaryResponseModel>(Object);
  const [pageIndex, setPageIndex] = useState(
    PaginationSettings.initialPageIndex
  );
  const currentSession = useAppSelector((state: VRIAppStateType) => ({
    profile: state.auth.profile,
    userRole: state.auth.profile?.userRole,
    hasSeenModal: state.auth.profile?.hasSeenModal,
    callStatus: state.videoCall.CallStatus,
  }));
  const { controlBrowserBackButton } = controlBrowserBackAndReloadButton();
  const [pageSize, setPageSize] = useState(PaginationSettings.initialPageSize);
  const [searchText, setSearchText] = useState<string>('');
  const [callLogData, setCallLogData] =
    useState<CallLogPagedResponseModel>(Object);
  const [pagedCallListCount, setPagedCallListCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [promptedThisSession, setPromptedThisSession] = useState(false);
  const location = useLocation<{
    callId: string;
    bookingId: string;
    CallStatus: CallStatusType;
  }>();
  const callId = location.state?.callId;
  const bookingId = location.state?.bookingId;
  const { LoadById } = useFunctionalityCallSummary();
  const { LoadBookingSummaryById } = useFunctionalityBookingSummary();
  const [menuSettings, setMenuSettings] = useState<ApiSchemaMenuSettingsData>();
  const { stopBothVideoAndAudio } = useFunctionalityMediaPermissions();
  const dispatch = useAppDispatch();
  const { resumeAudioAndRegister } = useContext(AudioDeviceContext);



  const getCallLogType = () => {
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return CallLogType.CallLog;
    else return CallLogType.InternalCall;
  };

  const [columnSelection, setColumnSelection] = useState<
    ColumnSelectionsModel[]
  >([]);
  const [columnSelectionModalShow, setColumnSelectionModalShow] =
    React.useState(false);
  const isAdminUser = currentSession.userRole === AppConstants.UserRoles.Admin;
  const columnSelectionForModal = isAdminUser
    ? columnSelection
    : columnSelection.filter(
        (eachColumn) => eachColumn.propertyName.toLowerCase() !== 'sid'
      );
  const getColumnSelectionsType = () => {
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return ColumnSelectionTableType.CallLog;
    else return ColumnSelectionTableType.InternalCall;
  };
  const { LoadColumnSelections } = useFunctionalityColumnSelections({
    type: getColumnSelectionsType(),
  });

  useEffect(() => {
    LoadColumnSelections().then((r) => {
      if (r?.data) setColumnSelection(r?.data);
    });
  }, []);

  useEffect(() => {
    stopBothVideoAndAudio();
  }, [callSummaryData]);

  const { callLogColumn } = useFunctionalityCallLogColumns({
    userRole: currentSession.userRole ?? '',
    setShow: setShow,
    setCallSummaryData: setCallSummaryData,
    setLoaderProgress: setLoaderProgress,
    callLogType: getCallLogType(),
    columnSelection: columnSelection,
  });

  const handleReadyClick = async () => {
    if (currentSession.profile) {
      const updatedProfile = { ...currentSession.profile, hasSeenModal: true };
      dispatch(ActionAuth.SetProfileData(updatedProfile));
    }

    await resumeAudioAndRegister();
    setShowModal(false);
  };

  let initialFilterData: ApiSchemaFilter = {
    filterQueries: [],
    sortQuery: {},
  };
  const [filterData, setFilterData] =
    useState<ApiSchemaFilter>(initialFilterData);
  const [filterDataChange, setFilterDataChange] = useState<number>(0);

  const { LoadCallLog } = useFunctionalityCallLog({
    pageSize: pageSize,
    pageNo: pageIndex,
    searchText: searchText?.trimStart(),
    callLogType: getCallLogType(),
    setLoaderProgress,
    filterData,
  });

  useEffect(() => {
    if (callId) {
      setLoaderProgress(topLoaderProgress.start);
      LoadById(callId).then((data) => {
        setLoaderProgress(topLoaderProgress.complete);
        if (data?.id) {
          setCallSummaryData(data);
          setShow(true);
        }
      });
    } else if (bookingId) {
      setLoaderProgress(topLoaderProgress.start);
      LoadBookingSummaryById(bookingId).then((data) => {
        setLoaderProgress(topLoaderProgress.complete);
        if (data) setCallSummaryData(data);
        setShow(true);
      });
    }
    setLoaderProgress(topLoaderProgress.start);
    LoadCallLog().then((r) => {
      setLoaderProgress(topLoaderProgress.complete);

      if (r?.data) {
        setCallLogData(r?.data);
        setPagedCallListCount(r?.data.totalCount);
      }
    });
  }, [pageSize, pageIndex, callId, bookingId, filterData, searchText]);

  useEffect(() => { }, [menuSettings]);
  const { getMenusettings } = useFunctionalityUserProfile();
  useEffect(() => {
    if (currentSession.userRole == AppConstants.UserRoles.Operator) {
      setLoaderProgress(topLoaderProgress.start);
      getMenusettings().then((d) => {
        if (d) setMenuSettings(d);
        setLoaderProgress(topLoaderProgress.complete);
      });
    }
  }, []);

  const onChangePageIndex = (index: number) => {
    setPageIndex(index);
  };
  const onChangePageSize = (size: number) => {
    setPageSize(size);
  };
  const handleSearch = (e: any) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSearchText(e.target.value);
      setPageIndex(1);
    });
  };

  const getExportTableType = () => {
    if (
      currentSession.userRole === AppConstants.UserRoles.Operator ||
      currentSession.userRole === AppConstants.UserRoles.User ||
      currentSession.userRole === AppConstants.UserRoles.CompanyAdmin ||
      currentSession.userRole === AppConstants.UserRoles.CompanyFinance
    )
      return ExportTableType.CallLog;
    else return ExportTableType.InternalCall;
  };

  useEffect(() => {
    if (
      location.state?.CallStatus === CallStatusType.Canceled ||
      currentSession.callStatus?.current === CallStatusType.Rejected
    ) {
      controlBrowserBackButton();
    }
  }, [location.state?.CallStatus, currentSession.callStatus]);


  useEffect(() => {
    if (
      !isElectron() &&
      currentSession.userRole === AppConstants.UserRoles.Operator &&
      currentSession.profile &&
      !currentSession.profile.hasSeenModal &&
      !promptedThisSession
    ) {
      setShowModal(true);
      setPromptedThisSession(true);
    }
  }, [currentSession.profile, currentSession.userRole, promptedThisSession]);

  return (
    <>
      {!isElectron() && currentSession.userRole === AppConstants.UserRoles.Operator && showModal &&
        <div className="user-agent-click-overlay">
          <div className="ready-modal">
            <div className="ready-card shadow">
              <div className="ready-badge">⚡</div>
              <h5 className="mb-2">Ready to receive calls?</h5>
              <p className="text-muted mb-4">Click below to ensure browser readiness.</p>
              <button className="btn btn-primary px-4" onClick={handleReadyClick}>I&apos;m Ready</button>
            </div>
          </div>
        </div>
      }
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />
      <h4 className="my-3 font-weight-light">Internal Call</h4>
      <div className="call-log-page ">
        <div className="row">
          <div className="col-12">
            <Searchbar
              showFilter={true}
              showExportOptions={true}
              filterPanel={'commonFilterPanel'}
              searchText={searchText}
              handleSearch={handleSearch}
              columnSelectionModalShow={columnSelectionModalShow}
              setColumnSelectionModalShow={setColumnSelectionModalShow}
              columnSelection={columnSelectionForModal}
              setColumnSelection={setColumnSelection}
              showAutoRefresh={true}
              tableType={getExportTableType()}
              filterData={filterData}
              downloadedFileName={'CallLog'}
              setLoaderProgress={setLoaderProgress}
              setIsShowCommonFilter={setIsShowCommonFilter}
            />
          </div>
          {isShowCommonFilter ? (
            <CommonFilter
              setFilterData={setFilterData}
              setLoaderProgress={setLoaderProgress}
              setFilterDataChange={setFilterDataChange}
              pageName="callLogPage"
              pageType="internalCall"
              setPageIndex={setPageIndex}
            />
          ) : null}

          <CallLogInfoBox
            callLogData={callLogData}
            userRole={currentSession.userRole ?? ''}
          />
          <CallLogTableDataWeb
            pagingKey="PageCallLog"
            loaderProgress={loaderProgress}
            pageHeader="Call Log"
            isPageEnabled={true}
            tableData={callLogData.callLogs}
            tableHeaders={callLogColumn}
            pageIndex={pageIndex}
            pageSize={pageSize}
            count={pagedCallListCount}
            searchText={searchText}
            filterDataChange={filterDataChange}
            onChangePageIndex={onChangePageIndex}
            onChangePageSize={onChangePageSize}
            setCallSummaryData={setCallSummaryData}
            setLoaderProgress={setLoaderProgress}
            setShow={setShow}
          />
          <ModalCallSummary
            summary={callSummaryData}
            show={show}
            handleShow={setShow}
            onRefreshSummary={setCallSummaryData}
          />
        </div>
      </div>
    </>
  );
}
